import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { OktaAuth } from '@okta/okta-auth-js';
import { OktaSignIn } from '@okta/okta-signin-widget';

import { JwtHelperService } from "@auth0/angular-jwt";
import { Observable } from "rxjs";

import { MatDialog, MatDialogRef } from "@angular/material/dialog";

import { LoginErrorModalComponent } from "common/components/login-error-modal/login-error-modal.component";
import {
  AppLoadService,
  LoginResult,
  NotificationService,
  ADPResourceService as ResourceService,
  ResourcesLoaderService,
  SnackBarData, Snackbar,
  TokenInfo
} from 'digital-core';

import { BrowserGlobalService } from './BrowserNavigation/browser-global.service';
import { ConfigService } from './config.service';
import { HeaderFactoryService } from './header-factory.service';
import { LogoutBehaviorService } from './logout-behavior.service';
import { TokenService } from './token.service';

import { UnauthorizedSnackBarBodyComponent } from "../components/unauthorized-snack-bar-body/unauthorized-snack-bar-body.component";
import {
  AnalyticsPage,
  UserProfile
} from '../models';
import { GoogleAnalyticsService } from "./google-analytics.service";
import { PipeLineErrorService } from "./pipeLineError.service";
import { ProfileService } from "./profile.service";

@Injectable()
export class LoginService {

  loginResult: LoginResult;
  public applicationResources: any;
  public customUIElementResources: any;
  redirectTo = "";
  public securityModel: any = {};
  signInFailResult = false;
  securityWidget: any;
  securityWidgetOptions: any;
  webApiUrl: string;
  httpOptions = {
    headers: new HttpHeaders({
      Accept: "application/json"
    })
  };

  OktaAccessToken: string;
  OktaRefreshToken: string;

  constructor(

    private resourceLoaderService: ResourcesLoaderService,
    private router: Router,
    private configService: ConfigService,
    private tokenService: TokenService,
    private profileService: ProfileService,
    private zone: NgZone,
  
    private notificationService: NotificationService,
    private logoutBehaviorService: LogoutBehaviorService,
    private headerService: HeaderFactoryService,
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    private appLoadService: AppLoadService,
    private matDialog: MatDialog,
    private pipelineErrorService: PipeLineErrorService,
    private dialogRef: MatDialogRef<LoginErrorModalComponent>

  ) {
    this.webApiUrl = configService.apiUrl;
    this.initializeControl();
    this.logoutBehaviorService.logout().subscribe(
      res => {
        if (res) {
          this.signOut(true);
        }
      },
      err => {
        console.error(`An error occurred: ${err.message}`);
      }
    );
    const profile = profileService.getUserProfile();
    const okta_auth_ApplicationName = this.getoktaAuthApplicationName();
    const accessToken = (okta_auth_ApplicationName && !profile) ? "" : this.tokenService.getAccessToken();
    if (!this.OktaAccessToken && accessToken) {
      this.OktaAccessToken = accessToken.accessToken;
    }
    this.setAccessCode(this.OktaAccessToken);
  }

  setRefreshCode(RefreshToken: string) {
    this.OktaRefreshToken = RefreshToken;
    if (this.OktaRefreshToken) {
      this.tokenService.setRefreshToken(this.OktaRefreshToken);
    }
  }

  setAccessCode(OktaAccessToken: string) {
    this.OktaAccessToken = OktaAccessToken;
  }
  getoktaAuthApplicationName() {
    return this.profileService.getPortalAccessName();
  }
  getAuthClient() {
    const sessionOptions = {
      clientId: this.configService.oktaClientId,
      issuer: this.configService.oktaAuthIssuer,
      redirectUri: this.applicationResources ? this.applicationResources.redirectUri : ""
    };

    const authClient = new OktaAuth(sessionOptions);
    return authClient;
  }

  logOn() {

    //Do not redirect to login page if there is any GSP API pipeline error generated.
    if (this.pipelineErrorService.isPipelineError) {
      return;
    }
    const authClient = this.getAuthClient();
    // check if token is empty and check browser type and check token status
    //
    if (this.OktaAccessToken) {

      try {
        const helper = new JwtHelperService();
        const token = helper.decodeToken(this.OktaAccessToken);

        //Check if token is expired

        if (helper.isTokenExpired(this.OktaAccessToken)) {
          throw "Token Expired";
        }

        // building response format compatible with current code, maintain backward compatibility
        const res = [
          {
            claims: {
              email: token.email,
              name: token.email,
              preferred_username: token.sub,
              given_name: token.email,
              family_name: token.email,
            },
            expiresAt: token.exp,
            scope: token.scp,
            clientId: this.configService.oktaClientId,
            authorizeUrl: this.configService.oktaAuthorizeUrl,
            issuer: this.configService.oktaAuthIssuer
          },
          {
            accessToken: this.OktaAccessToken,
            authorizeUrl: this.configService.oktaAuthorizeUrl,
            expiresAt: token.exp,
            scope: token.scp,
            tokenType: "Bearer",
            userinfoUrl: this.configService.oktaAuthIssuer + "/v1/userinfo"
          }
        ];

        this.successLogin(res, true, true, null);
      }
      catch (e) {
        // Part of AUL integration
        this.signOut();
      }


    }
    // eventually this else statement has to go when removed widget
    else { // else goes through the widget
      authClient.session
        .get()
        .then(session => {
          if (
            session &&
            session.status === this.securityModel.consts.activeStatus
          ) {
            const oauthOptions = {
              responseType: this.applicationResources.responseType,
              scopes: this.applicationResources.scope,
              redirectUri: ""
            };

            if (
              this.applicationResources.redirectUri &&
              this.applicationResources.redirectUri.length > 0
            ) {
              oauthOptions.redirectUri = this.applicationResources.redirectUri;
            }

            authClient.token
              .getWithoutPrompt(oauthOptions)
              .then(tokenOrTokens => {
                this.successLogin(tokenOrTokens, true, true, null);
              })
              .catch(() => {
                // Part of AUL integration
                this.resolveNextLoginStep();
              });
          } else {
            // Part of AUL integration
            this.resolveNextLoginStep();
          }
        })
        .catch(() => {
          // Part of AUL integration
          this.resolveNextLoginStep();
        });

    }


  }

  refreshToken() {


    if (this.OktaAccessToken == '') {
      const authClient = this.getAuthClient();
      authClient.token
        .renew(this.tokenService.getIdToken())
        .then(session => {
          if (session) {
            this.tokenService.setIdToken(session);
            authClient.token
              .renew(this.tokenService.getAccessToken())
              .then(token => {
                if (token) {
                  this.tokenService.setAccessToken(token);
                } else {
                  this.signOut();
                }
              })
              .catch(() => {
                this.signOut();
              });
          } else {
            this.signOut();
          }
        })
        .catch(() => {
          this.signOut();
        });
    }
    else {
      const refreshToken = this.tokenService.getRefreshToken();
      if (refreshToken) {
        this.remoteRefreshToken().subscribe(data => {
          // transform api token to okta token format
          //Review or null to avoid error
          if (data !== null && data !== undefined && data.Scope !== null && data.Scope !== undefined) {
            const oktaTOken = {
              accessToken: data.AccessToken,
              authorizeUrl: this.configService.oktaAuthorizeUrl,
              expiresAt: data.ExpireIn,
              scope: data.Scope.split(" "),
              tokenType: data.TokenType,
              userinfoUrl: ""
            }
            this.tokenService.setAccessToken(oktaTOken);
          }

        })
      }

    }

  }

  private remoteRefreshToken(): Observable<any> {
    var refreshToken = this.tokenService.getRefreshToken();
    return this.http
      .post<any>(
        `${this.configService.apiUrl}api/v${this.configService.apiVersion}/Auth/token/refresh`,
        {
          refreshToken
        },
        this.headerService.getBasicHeaderOptions()
      );
  }

  isValidTokenAsync(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const profile = this.tokenService.getCurrentUserInfo();
      const token = this.tokenService.getAccessToken();
      const helper = new JwtHelperService();

      if (token) {     // check if token did not refresh somehow, and cleaning the app and redirecting back
        if (profile && helper.isTokenExpired(token.accessToken)) {
          this.signOut();
          return observer.next(false);
        } else {
          return observer.next(true);
        }
      } else {
        return observer.next(false);
      }
    });
  }



  /**
   *Sign out
   *
   * @memberof OktaService
   */
  public signOut(reload: boolean = false) {
    localStorage.removeItem('login_info');
    localStorage.removeItem("dealerShip_info");
    localStorage.removeItem('local_resources_menu_Portal_Left_Menu');
    localStorage.removeItem('token_info');
    localStorage.removeItem("access_token");
    localStorage.removeItem('id_token');
    localStorage.removeItem("isAUL");
    localStorage.removeItem('current_language');
    localStorage.removeItem('available_languages');
    localStorage.removeItem('okta_auth_application_Name');
    sessionStorage.clear();
    this.AULsignOut();
  }

  // Part of AUL integration
  public AULsignOut() {
    this.zone.run(async () => {
      this.document.location.href = this.configService.aulLogOutEndpoint();
    });
  }

  // Part of AUL integration
  public AULAddProfile(queryString: any) {
    this.zone.run(async () => {
      this.document.location.href = this.configService.aulAddProfileEndpoint(this.document.location.href, queryString);
    });
  }

  showWidGet() {
    this.logOn();
  }


  // Part of AUL integration
  resolveNextLoginStep() {
    this.document.location.href = this.configService.aulLoginEndpoint();
  }

  /**
   *Handles the success login
   *
   * @param res
   * @param helpers
   * @param consts
   * @param values
   * @memberof OktaService
   */
  private successLogin(res, searchProfileByToken = true, redirectToIndex = true, callback: () => any) {
    const scope = this;
    const helpers = this.securityModel.helpers;
    const consts = this.securityModel.consts;
    this.loginResult = new LoginResult();
    const recoveryTokenValue = helpers.getElementValue(
      consts.recoveryModeTextBoxId,
      consts.emptyString
    );
    this.loginResult.RecoveryToken = recoveryTokenValue;
    const profile = new UserProfile();
    let userName: string = res[0].claims.preferred_username;
    if (userName.endsWith(consts.lanIdSuffix)) {
      userName = userName.replace(consts.lanIdSuffix, "");
    }
    this.loginResult.Login = userName;
    this.loginResult.ExpiresAt = new Date(Number(res[0].expiresAt) * 1000);
    profile.UserName = userName;
    profile.Email = res[0].claims.email;
    if (res[0].claims.given_name) {
      profile.FirstName = res[0].claims.given_name;
      profile.LastName = res[0].claims.family_name;
    } else if (res[0].claims.name) {
      const name = res[0].claims.name.split(' ');
      profile.FirstName = name[0];
      profile.LastName = name[1];
    }
    // AUL Integration
    const tokenInfo = new TokenInfo();
    tokenInfo.AccessToken = res[1];
    tokenInfo.IdToken = res[0];
    this.tokenService.setTokenInformation(tokenInfo);
    const aulResponse = this.profileService.getUserProfileFromAUL(profile, searchProfileByToken).subscribe((result) => {
      const profileResponse: UserProfile = this.AsUserProfile(aulResponse, profile);
      this.profileService.getProfile(profileResponse).subscribe(
        response => {
          if (response && response.Valid) {
            if (this.securityWidget !== undefined && this.securityWidget !== null) {
              this.securityWidget.remove();
            }
            // set user default Role
            if (!!result && !!result['MS1 DEFAULT ROLE']) {
              response.UserProfile.DefaultRole = result['MS1 DEFAULT ROLE'];
            }
            // set user profile
            this.profileService.setUserProfile(response.UserProfile);
            if (response.UserProfile.Program == "ACC") {
              window['config'].core.clientCMS = window['config'].preneed.accClientCMS;
              window['config'].core.program = window['config'].preneed.accProgram;
            } else if (response.UserProfile.Program == "FE") {
              window['config'].core.clientCMS = window['config'].preneed.feClientCMS;
              window['config'].core.program = window['config'].preneed.feProgram;
            }
            this.appLoadService.loadResources().then(() => {
              if (redirectToIndex) {
                scope.zone.run(async () => {
                  await scope.resourceLoaderService.loadVersion();
                  scope.navigateToDefaultPage();
                });
              }
            });
            if (callback) {
              callback();
            }
          } else {
            this.loginFail();
          }
        },
        (error) => {
          this.displayError(error, profile, result);
        }
      )
    });
  }
  displayError(error, profile, aulProfileresponse) {
    const strProperty = [];
    if (profile) {
      if (aulProfileresponse) {
        Object.keys(aulProfileresponse).forEach((key) => {
          if (
            key !== "Applications" &&
            key !== "ApplicationRoles" &&
            key !== "OktaGroups"
          ) {
            strProperty.push(key + ": " + aulProfileresponse[key]);
          }
        });
      }
    }

    if (strProperty.length === 0) {
      return;
    }
    const modalData = {
      header: "Error Message",
      errorMessage: error,
      aulProfile: strProperty
    };

    this.dialogRef = this.matDialog.open(LoginErrorModalComponent, {
      height: 'auto',
      data: modalData,

    });
    // Delete application item
    this.dialogRef.afterClosed().subscribe((result) => {
      if (result && result.toLowerCase() === "close") {
        this.loginError(error, profile);
      }
    });
  }
  private navigateToDefaultPage() {
      const routeName = this.configService.getPageRedirectPostLogin || 'sales';
    this.router.navigate([routeName]);
  }

  loginError(error: any, profile: UserProfile) {

    if (error.status.toString() !== "401") {
      this.loginFail();
    } else {

      var errorMessage = '';
      if (error?.error && error?.error.Message) {
        errorMessage = error.error.Message;
      } else {
        errorMessage = error;
      }

      this.notificationService.showNotification(
        UnauthorizedSnackBarBodyComponent,
        new SnackBarData(
          errorMessage,
          "",
          'error', profile.Email
        ),
        new Snackbar('error', true, false, 9000000)
      );
    }

  }

  AsUserProfile(contentResponse: any, profileInput: any): UserProfile {

    const profile: UserProfile = new UserProfile();
    profile.UserName = profileInput.UserName;
    profile.Email = profileInput.Email;
    return profile;
  }


  refreshSessionWithValidate(callback: () => any, redirectToIndex) {

    const authClient = this.getAuthClient();

    if (this.OktaAccessToken) {

      try {
        const helper = new JwtHelperService();
        const token = helper.decodeToken(this.OktaAccessToken);

        //Check if token is expired

        if (helper.isTokenExpired(this.OktaAccessToken)) {
          throw "Token Expired";
        }

        // building response format compatible with current code, maintain backward compatibility
        const res = [
          {
            claims: {
              email: token.email,
              name: token.email,
              preferred_username: token.sub,
              given_name: token.email,
              family_name: token.email,
            },
            expiresAt: token.exp,
            scope: token.scp,
            clientId: this.configService.oktaClientId,
            authorizeUrl: this.configService.oktaAuthorizeUrl,
            issuer: this.configService.oktaAuthIssuer
          },
          {
            accessToken: this.OktaAccessToken,
            authorizeUrl: this.configService.oktaAuthorizeUrl,
            expiresAt: token.exp,
            scope: token.scp,
            tokenType: "Bearer",
            userinfoUrl: this.configService.oktaAuthIssuer + "/v1/userinfo"
          }
        ];

        this.successLogin(res, true, false, callback);
      }
      catch (e) {
        // Part of AUL integration
        this.signOut();
      }


    } else {

      authClient.session
        .get()
        .then(session => {
          if (
            session &&
            session.status === this.securityModel.consts.activeStatus
          ) {

            const oauthOptions = {
              responseType: this.applicationResources.responseType,
              scopes: this.applicationResources.scope,
              redirectUri: ""
            };

            if (
              this.applicationResources.redirectUri &&
              this.applicationResources.redirectUri.length > 0
            ) {
              oauthOptions.redirectUri = this.applicationResources.redirectUri;
            }

            authClient.token
              .getWithoutPrompt(oauthOptions)
              .then(tokenOrTokens => {
                this.successLogin(tokenOrTokens, true, redirectToIndex, callback());
                callback();
              })
              .catch(() => {

              });


          }
        })
        .catch((err) => {

        });
    }
  }

  getWidget() {
    const helpers = this.securityModel.helpers;
    const consts = this.securityModel.consts;
    const values = this.applicationResources;
    this.securityWidgetOptions = this.getSecurityWidgetOptions(helpers, consts, values);
    if (values.redirectUri && values.redirectUri.length > 0) {
      this.securityWidgetOptions.redirectUri = values.redirectUri;
    }
    this.securityWidget = values.useInternal
      ? new OktaSignIn(this.securityWidgetOptions)
      : new OktaSignIn(this.securityWidgetOptions);
    return this.securityWidget;
  }


  /**
   *This method will initiallize all the objects required to create he okta widgwet
   *
   * @memberof OktaService
   */
  private initializeControl() {
    this.redirectTo = "index";
    this.applicationResources = {
      useInternal: true,
      smsRecovery: false,
      selfServiceUnlock: false,
      actionUrl: "/public/login",
      redirectUri: this.getLoginAbsoluteUrl(),
      baseUrl: this.configService.oktaBaseUrl,
      clientId: this.configService.oktaClientId,
      internalEndpoint: this.configService.oktaInternalEndPoint,
      language: "en-US",
      usernamePrefix: this.configService.oktaPrefix, // Mandatory parameter.
      oktaAuthorizeUrl: this.configService.oktaAuthorizeUrl ? this.configService.oktaAuthorizeUrl : null,
      oktaAuthIssuer: this.configService.oktaAuthIssuer ? this.configService.oktaAuthIssuer : null
    };

    this.securityModel.consts = this.securityModel.consts || {};

    this.loadConsts();

    this.securityModel.helpers = this.securityModel.helpers || {};

    this.loadHelpers();

    this.loadValues();
  }

  // we dont need this functionl.
  private toggleRecoveryModePanel(hidden) {
    const helpers = this.securityModel.helpers;
    const consts = this.securityModel.consts;
    const values = this.applicationResources;

    helpers.toggleElementVisibility(consts.recoveryModePanelId, hidden);

    helpers.setElementText(
      consts.recoveryModeHeaderId,
      values.recoveryModeHeaderText
    );

    helpers.setElementText(
      consts.recoveryModeButtonId,
      values.recoveryModeButtonText
    );

    const recoveryModeButtonElement = document.getElementById(
      consts.recoveryModeButtonId
    );

    if (recoveryModeButtonElement) {
      recoveryModeButtonElement.onclick = () => {
        const result =
          !values.onValidateRecoveryToken || values.onValidateRecoveryToken();
        if (result) {
          const recoveryTokenValue = helpers.getElementValue(
            consts.recoveryModeTextBoxId,
            consts.emptyString
          );
          values.recoveryToken = helpers.convertToString(recoveryTokenValue);
          this.toggleSecurityWidget(false);
        }
      };
    }
  }
  /**
   *Shows/ hide the widget ( including the widget creation )
   *
   * @param  hidden
   * @memberof OktaService
   */
  public toggleSecurityWidget(hidden) {
    const failed = localStorage.getItem("login_fail");
    if (failed) {
      this.setSignInFailResult(true);
      localStorage.removeItem("login_fail");
    } else {
      this.setSignInFailResult(false);
      // this.oktaService.signInFailResult = false;
    }

    const helpers = this.securityModel.helpers;
    const consts = this.securityModel.consts;
    const values = this.applicationResources;

    this.toggleRecoveryModePanel(!hidden);

    this.securityWidget = this.getWidget();

    if (hidden === true) {
      this.securityWidget.hide();
    } else {
      this.securityWidget.renderEl(
        {
          el: helpers.getElementIdSelector(consts.securityWidgetId)
        },
        res => {
          const result =
            !values.onSuccessCallback || values.onSuccessCallback(res);
          if (result) {
            if (res.status === consts.forgotPasswordEmailSentStatus) {
              this.toggleSecurityWidget(false);
            } else {
              if (res.status === consts.successStatus) {
                this.successLogin(res, true, true, null);
              }
            }
          }
        },
        err => {
          const result = !values.onErrorCallback || values.onErrorCallback(err);
          if (result) {
          }
        }
      );

      // setting callback events on render
      this.securityWidget.on(consts.pageRenderedEvent, () => {
        if (
          !helpers.isNonEmptyString(this.securityWidgetOptions.helpLinks.help)
        ) {
          const helpLink = document.querySelector(consts.helpLinkClassSelector);
          helpers.addClass(helpLink, consts.hiddenClass);
        }

        if (values.interceptBack) {
          const backLink = document.querySelector(consts.backLinkClassSelector);
          if (backLink) {
            backLink.addEventListener(consts.clickEvent, e => {
              e.preventDefault();
              e.stopPropagation();
              const selfUrl = helpers.isNonEmptyString(values.selfUrl)
                ? values.selfUrl
                : values.actionUrl;
              window.location.assign(selfUrl);
            });
          }
        }
        const result =
          !values.onWidgetRenderedCallback || values.onWidgetRenderedCallback();
        if (result) {
        }
      });
    }
  }

  /**
   *Set if the Okta widget should be rendered as invalid
   *
   * @param result
   * @memberof OktaService
   */
  public setSignInFailResult(result) {
    this.signInFailResult = result;
    this.customUIElementResources.SignInFailedResult = result;
  }

  /*Utils*/

  /**
   *Generate the absolute url for the login Url.
   *
   * @returns
   * @memberof OktaService
   */
  private getLoginAbsoluteUrl() {
    const internalUrl = "/Public/Login";
    const absoluteUrl =
      location.origin + this.router.createUrlTree([internalUrl]);
    return absoluteUrl;
  }

  // Initializacion methods
  /**
   *Load the resources associated to the widget ( using ghe local resource manager )
   *
   * @memberof OktaService
   */
  // private loadCustomUI() {
  //   this.customUIElementResources = {
  //     Login: this.resourceService.getViewResource("Login_Text_Title"),
  //     SignInFailedResult: this.signInFailResult,
  //     SignInFailed: this.resourceService.getViewResource(
  //       "Login_Text_SignInFailed"
  //     ),
  //     LockOut: this.resourceService.getViewResource("Login_Text_LockOut"),
  //     ForgotPassword: this.resourceService.getViewResource(
  //       "Login_Text_ForgotPassword"
  //     ),
  //     PasswordReset: this.resourceService.getViewResource(
  //       "Login_Text_PasswordReset"
  //     ),
  //     ReturnToLogin: this.resourceService.getViewResource(
  //       "Login_Text_ReturnToLogin"
  //     ),
  //     ForgotPasswordSubtitle: this.resourceService.getViewResource(
  //       "Login_Text_ForgotPasswordSubtitle"
  //     ),
  //     PasswordResetMainButtonText: this.resourceService.getViewResource(
  //       "Login_Text_PasswordResetMainButtonText"
  //     ),
  //     PasswordResetEmailSentTitle: this.resourceService.getViewResource(
  //       "Login_Text_PasswordResetEmailSentTitle"
  //     ),
  //     PasswordResetEmailSentSubTitle: this.resourceService.getViewResource(
  //       "Login_Text_PasswordResetEmailSentSubTitle"
  //     ),
  //     PasswordResetEmailSentBackButton: this.resourceService.getViewResource(
  //       "Login_Text_PasswordResetEmailSentBackButton"
  //     ),
  //     PasswordResetNewPasswordPlaceholder: this.resourceService.getViewResource(
  //       "Login_Text_PasswordResetNewPasswordPlaceholder"
  //     ),
  //     CurrentPassword: this.resourceService.getViewResource(
  //       "Login_Text_PasswordResetCurrentPasswordPlaceHolder"
  //     ),
  //     PasswordResetConfirmPasswordPlaceholder: this.resourceService.getViewResource(
  //       "Login_Text_PasswordResetConfirmPasswordPlaceholder"
  //     ),
  //     PasswordResetAnswerPlaceholder: this.resourceService.getViewResource(
  //       "Login_Text_PasswordResetAnswerPlaceholder"
  //     ),
  //     LoginEmailPlaceholder: this.resourceService.getViewResource(
  //       "Login_Text_LoginEmailPlaceholder"
  //     ),
  //     LoginPasswordPlaceholder: this.resourceService.getViewResource(
  //       "Login_Text_LoginPasswordPlaceholder"
  //     ),
  //     LoginVerifyCredentials: this.resourceService.getViewResource(
  //       "Login_Text_LoginVerifyCredentials"
  //     ),
  //     PasswordResetRules: this.resourceService.getViewResource(
  //       "Login_Text_PasswordResetRules"
  //     )
  //   };
  // }

  /**
   *Create all the helpers used by the Okta widget
   *
   * @memberof OktaService
   */
  private loadHelpers() {
    const helpers = this.securityModel.helpers;
    const consts = this.securityModel.consts;

    helpers.isString = function (value) {
      return value instanceof String || typeof value === consts.stringLiteral;
    };

    helpers.isNonEmptyString = function (value) {
      return helpers.isString(value) && value.length > 0;
    };

    helpers.isNumber = function (value) {
      return (
        (value instanceof Number || typeof value === consts.numberLiteral) &&
        !isNaN(value)
      );
    };

    helpers.isNonNegativeNumber = function (value) {
      return this.isNumber(value) && value >= 0;
    };

    helpers.isBoolean = function (value) {
      return value instanceof Boolean || typeof value === consts.booleanLiteral;
    };

    helpers.isFunction = function (value) {
      return typeof value === consts.functionLiteral;
    };

    helpers.isAgentId = function (value) {
      const expression = consts.agentIdRegularExpression;
      return expression.test(value);
    };

    helpers.isEmail = function (value) {
      const expression = consts.emailRegularExpression;
      return expression.test(value);
    };

    helpers.isCustomNull = function (value) {
      return helpers.isString(value) ? value === consts.customNull : false;
    };

    helpers.isArray = function (value, minLength, typeFunction) {
      let result = false;
      if (
        value instanceof Array &&
        (!minLength ||
          (this.isNonNegativeNumber(minLength) && value.length >= minLength))
      ) {
        result = true;
        let index = 0;
        while (
          index <
          Math.min(!minLength ? value.length : minLength, value.length) &&
          typeFunction &&
          typeFunction(value[index])
        ) {
          index++;
        }

        result =
          index ===
          Math.min(!minLength ? value.length : minLength, value.length) ||
          !typeFunction;
      }

      return result;
    };

    helpers.convertToString = function (value, defaultValue) {
      let result = helpers.isString(defaultValue)
        ? defaultValue
        : consts.emptyString;
      if (helpers.isNonEmptyString(value)) {
        result = value;
      }

      return !helpers.isCustomNull(result) ? result : undefined;
    };

    helpers.convertToBoolean = function (value, defaultValue) {
      let result =
        (defaultValue &&
          helpers.isString(defaultValue) &&
          defaultValue.toLowerCase() === consts.trueValue) ||
        (helpers.isBoolean(defaultValue) && defaultValue);
      if (value && helpers.isString(value)) {
        result = value.toLowerCase() === consts.trueValue;
      } else {
        if (helpers.isBoolean(value)) {
          result = value;
        }
      }

      return result;
    };

    helpers.convertToArray = function (value, defaultValue) {
      const defaultArray = helpers.isString(defaultValue)
        ? helpers.convertToArray(defaultValue, [])
        : [];
      let result = defaultArray;
      if (helpers.isNonEmptyString(value)) {
        result = [];
        const tokens = value.split(consts.tokensSeparator);
        for (let index = 0; index < tokens.length; index++) {
          if (
            helpers.isString(tokens[index]) &&
            tokens[index] !== consts.emptyString
          ) {
            result.push(tokens[index]);
          }
        }
      } else {
        if (helpers.isArray(value, 1)) {
          result = [];
          for (let index = 0; index < value.length; index++) {
            if (
              helpers.isString(value[index]) &&
              value[index] !== consts.emptyString
            ) {
              result.push(value[index]);
            }
          }
        }
      }

      return result;
    };

    helpers.convertToFunction = function (value, defaultValue) {
      let result =
        (helpers.isString(defaultValue) ? window[defaultValue] : undefined) ||
        (helpers.isFunction(defaultValue) ? defaultValue : undefined);
      if (helpers.isString(value)) {
        result = window[value];
      } else {
        if (helpers.isFunction(value)) {
          result = value;
        }
      }

      return result;
    };

    helpers.getElementIdSelector = function (id) {
      let result = consts.emptyString;
      if (helpers.isString(id)) {
        const prefix =
          id.indexOf(consts.elementIdSelector) === 0
            ? consts.emptyString
            : consts.elementIdSelector;
        result = prefix + id;
      }

      return result;
    };

    helpers.getParameterByName = function (name, url) {
      let result = consts.emptyString;
      if (name) {
        url = !url ? window.location.href : url;
        name = name.replace(/[\[\]]/g, "\\$&");
        const pattern = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
        const tokens = pattern.exec(url);
        if (!tokens) {
          return null;
        }
        if (!tokens[2]) {
          return consts.emptyString;
        }
        result = decodeURIComponent(result[2]);
      }

      return decodeURIComponent(name);
    };

    helpers.getElementValue = function (elementId, value) {
      const element = document.getElementById(elementId);
      return element ? element.nodeValue : value;
    };

    helpers.setElementValue = function (id, value) {
      const element = document.getElementById(id);
      if (element && value) {
        element.nodeValue = value;
      }
    };

    helpers.submitForm = function (id, actionValue) {
      const element = document.getElementById(id);
      if (element) {
        element.setAttribute(consts.actionAttribute, actionValue);
        // element.submit();
      }
    };

    helpers.toggleElementVisibility = function (elementId, hidden) {
      const element = document.getElementById(elementId);
      if (element) {
        element.classList.toggle(consts.hiddenClass, hidden);
      }
    };

    helpers.setElementText = function (id, text) {
      const element = document.getElementById(id);
      if (element) {
        element.innerText = text;
      }
    };

    helpers.concatUrls = function (baseUrl, url) {
      let left = helpers.isNonEmptyString(baseUrl)
        ? baseUrl
        : consts.emptyString;
      left +=
        helpers.isNonEmptyString(left) &&
          left.charAt(left.length - 1) !== consts.urlPostfix
          ? consts.urlPostfix
          : consts.emptyString;
      let right = helpers.isNonEmptyString(url) ? url : consts.emptyString;
      right =
        helpers.isNonEmptyString(left) &&
          helpers.isNonEmptyString(right) &&
          right.charAt(0) === consts.urlPostfix
          ? right.substring(1)
          : right;
      return left + right;
    };

    helpers.hasClass = function (element, classValue) {
      let result = false;
      if (element && helpers.isNonEmptyString(classValue)) {
        const pattern = new RegExp("(\\s|^)" + classValue + "(\\s|$)");
        result = !!element.className.match(pattern);
      }

      return result;
    };

    helpers.addClass = function (element, classValue) {
      if (element) {
        const value = helpers.hasClass(element, classValue)
          ? consts.emptyString
          : " " + classValue;
        element.className += value;
      }
    };

    helpers.removeClass = function (element, classValue) {
      if (element && helpers.isNonEmptyString(classValue)) {
        if (helpers.hasClass(element, classValue)) {
          const pattern = new RegExp("(\\s|^)" + classValue + "(\\s|$)");
          element.className = element.className.replace(pattern, " ");
        }
      }
    };
  }

  /**
   *Load all the constants used by The Widget
   *
   * @memberof OktaService
   */
  private loadConsts() {
    const consts = this.securityModel.consts;
    consts.emptyString = "";
    consts.emptyArray = [];
    consts.emptyObject = {};

    consts.stringLiteral = "string";
    consts.numberLiteral = "number";
    consts.booleanLiteral = "boolean";
    consts.functionLiteral = "function";
    consts.emailRegularExpression = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    consts.agentIdRegularExpression = /^[0-9]{5}$/;
    consts.trueValue = "true";
    consts.elementIdSelector = "#";
    consts.securityWidgetId = "securityWidget";
    consts.customNull = "[NULL]";
    consts.defaultAuthParamsDisplay = "popup"; // "page"; // "popup";
    consts.defaultResponseMode = "okta_post_message"; // "form_post"; // "query"; // "okta_post_message";
    consts.defaultResponseType = "id_token,token";
    consts.defaultScope = "openid,email,profile,address,phone";
    consts.defaultAuthScheme = "OAUTH2";
    consts.defaultBaseUrl = "https:dev-//assurant.oktapreview.com";
    consts.englishLanguage = "en";
    consts.successStatus = "SUCCESS";
    consts.activeStatus = "ACTIVE";
    consts.relayStateParameterName = "RelayState";
    consts.securityFormId = "securityForm";
    consts.recoveryTokenFieldId = "recoveryToken";
    consts.idTokenFieldId = "idToken";
    consts.actionAttribute = "action";
    consts.tokensSeparator = ",";
    consts.recoveryModePanelId = "recoveryModePanel";
    consts.recoveryModeHeaderId = "recoveryModeHeader";
    consts.recoveryModeHeaderText =
      "Please introduce the recovery token sent via email.";
    consts.recoveryModeTextBoxId = "recoveryModeTextBox";
    consts.recoveryModeButtonId = "recoveryModeButton";
    consts.recoveryModeButtonText = "Submit";
    consts.hiddenClass = "hiddenClass";
    consts.forgotPasswordEmailSentStatus = "FORGOT_PASSWORD_EMAIL_SENT";
    consts.sessionIdFieldId = "sessionId";
    consts.lanIdSuffix = "@CEAD.TST";
    consts.infix = "__";
    consts.defaultInternalEndpoint = "http://id-dev.assurant.com";
    consts.urlPostfix = "/";
    consts.forgotPasswordEndpointPostfix = "/api/recovery/password";
    consts.recoveryTokenParameterName = "recoveryToken";
    consts.pageRenderedEvent = "pageRendered";
    consts.clickEvent = "click";
    consts.helpLinkClassSelector = "a[data-se='help-link']";
    consts.backLinkClassSelector = "a[data-se='back-button']";
  }

  /**
   *Load all the values required by the widget
   *
   * @memberof OktaService
   */
  private loadValues() {
    const values = this.applicationResources;
    const helpers = this.securityModel.helpers;
    const consts = this.securityModel.consts;
    values.usernamePrefix = helpers.convertToString(values.usernamePrefix);
    values.internalEndpoint = helpers.convertToString(
      values.internalEndpoint,
      consts.defaultInternalEndpoint
    );
    values.recoveryTokenParameterName = helpers.convertToString(
      values.recoveryTokenParameterName,
      consts.recoveryTokenParameterName
    );
    values.authParamsDisplay = helpers.convertToString(
      values.authParamsDisplay,
      consts.defaultAuthParamsDisplay
    );
    values.responseMode = helpers.convertToString(
      values.responseMode,
      consts.defaultResponseMode
    );
    values.responseType = helpers.convertToArray(
      values.responseType,
      consts.defaultResponseType
    );
    values.scope = helpers.convertToArray(values.scope, consts.defaultScope);
    values.authScheme = helpers.convertToString(
      values.authScheme,
      consts.defaultAuthScheme
    );
    values.baseUrl = helpers.convertToString(
      values.baseUrl,
      consts.defaultBaseUrl
    );
    values.clientId = helpers.convertToString(values.clientId);
    values.autoPush = helpers.convertToBoolean(values.autoPush, true);
    values.callRecovery = helpers.convertToBoolean(values.callRecovery, false);
    values.hideSignOutLinkInMFA = helpers.convertToBoolean(
      values.hideSignOutLinkInMFA,
      false
    );
    values.multiOptionalFactorEnroll = helpers.convertToBoolean(
      values.multiOptionalFactorEnroll,
      true
    );
    values.rememberMe = helpers.convertToBoolean(values.rememberMe, false);
    values.router = helpers.convertToBoolean(values.router, false);
    values.selfServiceUnlock = helpers.convertToBoolean(
      values.selfServiceUnlock,
      true
    );
    values.smsRecovery = helpers.convertToBoolean(values.smsRecovery, true);
    values.windowsVerify = helpers.convertToBoolean(
      values.windowsVerify,
      false
    );
    values.helpLink = helpers.convertToString(values.helpLink);
    values.helpSupportNumber = helpers.convertToString(
      values.helpSupportNumber
    );
    values.language = helpers.convertToString(
      values.language,
      consts.englishLanguage
    );
    values.logo = helpers.convertToString(values.logo);
    values.recoveryToken = helpers.convertToString(values.recoveryToken);
    values.redirectUri = helpers.convertToString(values.redirectUri);
    values.addPrefix = helpers.convertToBoolean(values.addPrefix, true);
    values.onTransformUsername = helpers.convertToFunction(
      values.onTransformUsername
    );
    values.onSuccessCallback = helpers.convertToFunction(
      values.onSuccessCallback
    );
    values.onErrorCallback = helpers.convertToFunction(values.onErrorCallback);
    values.onWidgetRenderedCallback = helpers.convertToFunction(
      values.onWidgetRenderedCallback
    );
    values.selfUrl = helpers.convertToString(values.selfUrl);
    values.actionUrl = helpers.convertToString(values.actionUrl);
    values.recoveryMode = helpers.convertToBoolean(values.recoveryMode, false);
    values.recoveryModeHeaderText = helpers.convertToString(
      values.recoveryModeHeaderText,
      consts.recoveryModeHeaderText
    );
    values.recoveryModeButtonText = helpers.convertToString(
      values.recoveryModeButtonText,
      consts.recoveryModeButtonText
    );
    values.onValidateRecoveryToken = helpers.convertToFunction(
      values.onValidateRecoveryToken
    );
    values.closeSession = helpers.convertToBoolean(values.closeSession, false);
    values.useInternal = helpers.convertToBoolean(values.useInternal, true);
    values.filesEndpoint = helpers.convertToString(values.filesEndpoint);
    values.forgotPasswordEndpoint = consts.forgotPasswordEndpointPostfix;
    values.forgotPasswordEndpoint = helpers.concatUrls(
      values.internalEndpoint,
      values.forgotPasswordEndpoint
    );
    values.interceptBack = helpers.convertToBoolean(
      values.interceptBack,
      false
    );
  }

  /**
   *Gerenate the Widget Options object required in order to create the widget.
   *
   * @param helpers
   * @param consts
   * @param  values
   * @returns any
   * @memberof OktaService
   */
  private getSecurityWidgetOptions(helpers, consts, values): any {

    return {
      assurant: {
        appCode: values.usernamePrefix,
        forgotPasswordEndpoint: values.forgotPasswordEndpoint,
        recoveryTokenParam: values.recoveryTokenParameterName
      },
      authParams: {
        display: values.authParamsDisplay,
        responseType: values.responseType,
        responseMode: values.responseMode,
        scope: values.scope,
        issuer: values.oktaAuthIssuer,
        authorizeUrl: values.oktaAuthorizeUrl
      },
      authScheme: values.authScheme,
      baseUrl: values.baseUrl,
      clientId: values.clientId,
      features: {
        autoPush: values.autoPush,
        callRecovery: values.callRecovery,
        hideSignOutLinkInMFA: values.hideSignOutLinkInMFA,
        multiOptionalFactorEnroll: values.multiOptionalFactorEnroll,
        rememberMe: values.rememberMe,
        router: values.router,
        selfServiceUnlock: values.selfServiceUnlock,
        smsRecovery: values.smsRecovery,
        windowsVerify: values.windowsVerify
      },
      helpLinks: {
        help: values.helpLink
      },
      helpSupportNumber: values.helpSupportNumber,
      language: values.language,
      logo: helpers.concatUrls(values.filesEndpoint, values.logo),
      recoveryToken: values.recoveryToken,
      transformUsername: (username) => {
        username = values.onTransformUsername
          ? values.onTransformUsername(username)
          : username;
        const prefix =
          values.addPrefix &&
            username.indexOf(values.usernamePrefix + consts.infix) < 0 &&
            (helpers.isEmail(username) || helpers.isAgentId(username))
            ? values.usernamePrefix + consts.infix
            : consts.emptyString;
        if (helpers.isAgentId(username)) {
          username = username + '@oktauserdomain.com'
        }

        return prefix + username;
      }
    };
  }

  // Widget Events

  /**
   *Set the widget into an invalid state
   *
   * @memberof OktaService
   */
  private loginFail() {
    this.initializeControl();
    this.tokenService.setTokenInformation(null);
    localStorage.setItem("login_fail", "yes");
    this.httpsToHttpError();
    this.AULsignOut();
    // this.toggleSecurityWidget(false);
  }

  private httpsToHttpError() {
    const aulapiUrl = this.configService.aulApiUrl;
    const currentUri = this.document.location.href;
    // moving from https (portal url) to http (aulApiurl)
    if (!aulapiUrl.includes('https') && currentUri.includes('https')) {
      alert('Moving from Secure (Https) Portal to UnSecure (http) AUL, Please check configuration');
    }
  }
}